import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { jwtDecode } from "jwt-decode";
import { setUserId, clearUserId } from "../utils/googleanalytics";
import { User, AuthContextType } from "../utils/common";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const openAuthModal = () => setIsAuthModalOpen(true);
  const closeAuthModal = () => setIsAuthModalOpen(false);

  // 1. On Mount: Check localStorage for an existing session
  useEffect(() => {
    const savedCredential = localStorage.getItem("auth_token");
    if (savedCredential) {
      try {
        const decoded: User = jwtDecode(savedCredential);
        setUser(decoded);
        if (decoded.sub) {
          setUserId(decoded.sub);
        }
      } catch (error) {
        console.error("Invalid token found in storage");
        localStorage.removeItem("auth_token");
      }
    }
    setLoading(false);
  }, []);

  // 2. Login: Save the credential string to storage
  const login = (credential: string) => {
    localStorage.setItem("auth_token", credential);
    const decoded: User = jwtDecode(credential);
    setUser(decoded);
    console.log("User Logged In & Persisted:", decoded.name);
    // Attach GA user_id
    if (decoded.sub) {
      setUserId(decoded.sub);
    }
  };

  // 3. Logout: Clear storage
  const logout = () => {
    localStorage.removeItem("auth_token");
    setUser(null);
    clearUserId();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        openAuthModal,
        closeAuthModal,
        isAuthModalOpen,
      }}
    >
      {/* Only render children once we know if the user is logged in or not */}
      {!loading && children}
    </AuthContext.Provider>
  );
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
